import React, { useEffect, useMemo, useState } from 'react';
import { Layout, LayoutWithMainContent } from '@components/layout/Layout';
import { useWindowLocation } from '@hooks/Window/useWindowLocation';
import { InternalSearchCardType } from '@components/layout/InternalSearchMetaData';
import { ThemeHeroBlock } from '@components/SectionBlocks/blocks/ThemeHeroBlock/ThemeHeroBlock';
import { NAFGTMEvent } from '@appTypes/GTM/NAFGTMEvent';
import { useGTMDataLayer } from '@hooks/GTM/useGTMDataLayer';
import { MAIN_DESCRIPTION, MAIN_TITLE } from '@lib/constants';
import { Hero } from './components/Hero/Hero';
import { SectionBlocks } from '../../components/SectionBlocks';
import { useFrontPage } from '../../hooks/useFrontPage';
import { LoaderContent } from '../LoaderPage';
import { frontPagePath } from '../../apps/Themed/ThemedApp.utils';
import * as S from './FrontPage.styles';

export const FrontPage = ({ abTestWeight }: { abTestWeight?: number }) => {
  const dataLayer = useGTMDataLayer();
  const windowPath = useWindowLocation();
  const [tracked, setTracked] = useState(false);

  const { data } = useFrontPage(abTestWeight);

  const { hero, dynamicHero, content } = data || {};
  const dynamicHeroBlock = useMemo(() => ({ ...dynamicHero, isFrontPage: true }), [dynamicHero]);

  useEffect(() => {
    if (!tracked && data?.abTestOnPageExperimentDetails) {
      dataLayer.push({
        event: NAFGTMEvent.abExperimentParticipant,
        experimentId: data.abTestOnPageExperimentDetails.experimentId,
        variantId: data.abTestOnPageExperimentDetails.variantId,
      });
      setTracked(true);
    }
  }, [tracked, data?.abTestOnPageExperimentDetails, dataLayer]);

  if (!data) {
    return (
      <LayoutWithMainContent
        title={MAIN_TITLE}
        description={MAIN_DESCRIPTION}
        url={windowPath}
        internalSearchMetaData={{ cardType: InternalSearchCardType.Simple }}
      >
        <LoaderContent />
      </LayoutWithMainContent>
    );
  }

  return (
    <Layout
      title={MAIN_TITLE}
      description={MAIN_DESCRIPTION}
      url={frontPagePath}
      internalSearchMetaData={{ cardType: InternalSearchCardType.Simple }}
    >
      {hero ? <Hero hero={hero} /> : null}
      {dynamicHero ? (
        <S.FrontPageHeroWrapper>
          <ThemeHeroBlock block={dynamicHeroBlock} />
        </S.FrontPageHeroWrapper>
      ) : null}
      <S.SectionContainer>
        <SectionBlocks content={content} />
      </S.SectionContainer>
    </Layout>
  );
};
